import { useQuery } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import {
  PageHeader,
  PageContent,
  EmptyState,
  LoadingState,
} from '@printtat/ui';
import { adminApi } from '@/services/adminApi';
import type { EmailUsageSummary } from '@/services/adminApi';

const STATUS_COLOR = (pct: number): string => {
  if (pct >= 95) return 'text-red-700 bg-red-50';
  if (pct >= 80) return 'text-amber-700 bg-amber-50';
  if (pct >= 50) return 'text-yellow-700 bg-yellow-50';
  return 'text-emerald-700 bg-emerald-50';
};

const formatDay = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const Sparkline = ({
  data,
  width = 240,
  height = 56,
}: {
  data: { day: string; sends: number }[];
  width?: number;
  height?: number;
}) => {
  if (data.length === 0) return <div className="text-sm text-gray-400">No sends yet this period</div>;
  const max = Math.max(...data.map((d) => d.sends), 1);
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;
  const points = data
    .map((d, i) => `${i * stepX},${height - (d.sends / max) * height}`)
    .join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const AdminEmailUsagePage = () => {
  const { data, isLoading, error } = useQuery<EmailUsageSummary>({
    queryKey: ['admin', 'emailUsage'],
    queryFn: () => adminApi.emailUsage.summary(),
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <>
        <PageHeader title="Email volume" icon={Mail} />
        <PageContent>
          <LoadingState />
        </PageContent>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <PageHeader title="Email volume" icon={Mail} />
        <PageContent>
          <EmptyState
            title="Failed to load usage data"
            description={(error as Error)?.message ?? 'Unknown error'}
          />
        </PageContent>
      </>
    );
  }

  const headroomBadge = STATUS_COLOR(data.tierUsedPct);

  return (
    <>
      <PageHeader title="Email volume" icon={Mail} />
      <PageContent>
        <div className="grid gap-6 md:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Month-to-date
            </p>
            <p className="mt-2 text-3xl font-semibold text-gray-900">
              {data.monthToDateTotal.toLocaleString()}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              of {data.tierCap.toLocaleString()} tier cap
            </p>
            <span
              className={`mt-3 inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${headroomBadge}`}
            >
              {data.tierUsedPct}% used
            </span>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 md:col-span-2">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Daily volume (last 30 days)
            </p>
            <div className="mt-3 text-gray-900">
              <Sparkline data={data.daily} />
            </div>
          </div>
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">
              MTD by category
            </h3>
            {data.monthToDateByCategory.length === 0 ? (
              <p className="text-sm text-gray-500">No sends yet this month.</p>
            ) : (
              <div className="space-y-2">
                {data.monthToDateByCategory.map((row) => (
                  <div
                    key={row.category}
                    className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-b-0"
                  >
                    <span className="text-sm capitalize text-gray-700">
                      {row.category}
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {row.sends.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">
              Top users (last 7 days)
            </h3>
            {data.topUsers.length === 0 ? (
              <p className="text-sm text-gray-500">No sends in this window.</p>
            ) : (
              <div className="space-y-2">
                {data.topUsers.slice(0, 10).map((row) => (
                  <div
                    key={row.user_id}
                    className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-b-0"
                  >
                    <span className="truncate text-sm text-gray-700">
                      {row.email ?? row.user_id}
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {row.sends.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">
            Daily breakdown
          </h3>
          {data.daily.length === 0 ? (
            <p className="text-sm text-gray-500">No sends in the last 30 days.</p>
          ) : (
            <div className="grid grid-cols-7 gap-2 sm:grid-cols-10">
              {data.daily.map((row) => (
                <div
                  key={row.day}
                  className="flex flex-col items-center rounded border border-gray-100 p-2 text-center"
                >
                  <span className="text-xs text-gray-500">
                    {formatDay(row.day)}
                  </span>
                  <span className="mt-1 text-sm font-medium text-gray-900">
                    {row.sends.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </PageContent>
    </>
  );
};
